// Placements.tsx
"use client";

import Carousel8 from "@/components/organisms/carousels/Carousel8";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  iconClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo, useRef, useCallback } from "react";

interface PlacementMetadata {
  id: string;
  meta_image: string;
  meta_status: boolean;
  meta_alt_image?: string;
}

interface PlacementData {
  id: number;
  page_id: number;
  component_id: number;
  title: string;
  title_color?: string;
  background_color?: string;
  scroll_frequency?: number;
  metadata: PlacementMetadata[];
  variant: string;
  status: number;
}

const OPTIONS = { loop: false };

const Placements = ({ data }: { data: { data: PlacementData } }) => {
  const isMobile = useIsMobile(768);
  const ref = useRef<HTMLDivElement>(null);

  const {
    title,
    title_color,
    background_color,
    scroll_frequency = 1000,
    metadata = [],
  } = data?.data || {};

  const carouselSpeed = scroll_frequency ? scroll_frequency / 1000 : 1;

  const activeMetadata = useMemo(() => {
    return (metadata || []).filter(
      (item: PlacementMetadata) => item?.meta_status,
    );
  }, [metadata]);

  // Track widget viewed - MUST be called before early returns
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "placements_section");
  //   }
  // }, []);

  // Handle logo click with tracking
  const handleLogoClick = useCallback(
    (item: PlacementMetadata, index: number) => {
      iconClick({
        section: "placements_section",
        component: "placement_logo",
        entity: item.meta_alt_image || `Placement Logo ${index + 1}`,
        landing_url: "",
        landing_page_key: "",
      });
    },
    [],
  );

  if (
    !title ||
    !activeMetadata ||
    activeMetadata.length === 0 ||
    !data?.data?.status ||
    isMobile === null
  )
    return null;

  const half = Math.ceil(activeMetadata?.length / 2);
  const row1 = activeMetadata?.slice(0, half) || [];
  const row2 = activeMetadata?.slice(half) || [];

  return (
    <div
      ref={ref}
      className={cn(
        "py-8 sm:py-5",
        "flex justify-center items-center flex-col w-full overflow-hidden",
        "gap-spacing-14 sm:gap-spacing-20 md:gap-spacing-24",
      )}
      style={
        {
          // @ts-ignore
          backgroundColor: background_color || "#F74958",
          "--title-color": title_color || "#FFFFFF",
        } as React.CSSProperties
      }
    >
      <h2
        className={cn(
          "text-center w-full",
          "text-heading-6-semibold",
          "sm:w-[80%]",
          "md:text-heading-4-semibold",
          "lg:text-new-heading-34-semibold",
          "xl:text-new-heading-42-semibold",
          "line-clamp-1 overflow-hidden text-ellipsis",
          "type-h3",
        )}
        style={{ color: "var(--title-color)" }}
      >
        {title}
      </h2>

      <div className="w-full sm:w-[94%] mx-auto overflow-hidden px-4 sm:px-0">
        {isMobile ? (
          <div className="flex flex-col gap-3 w-full max-w-full">
            {row1.length > 0 && (
              <Carousel8
                speed={carouselSpeed}
                options={OPTIONS}
                allowManualSwipe={true}
              >
                {row1.map((item: PlacementMetadata, idx: number) =>
                  item?.meta_image ? (
                    <div
                      key={`row1-${item?.id}-${idx}`}
                      className="w-[82px] h-[42px] flex justify-center items-center shrink-0 cursor-pointer"
                      onClick={() => handleLogoClick(item, idx)}
                    >
                      <Image
                        src={item.meta_image}
                        alt={item?.meta_alt_image || "partner logo"}
                        width={82}
                        height={42}
                        className="object-contain w-[82px] h-[42px]"
                      />
                    </div>
                  ) : null,
                )}
              </Carousel8>
            )}

            {/**{row2.length > 0 && (
              <Carousel8
                speed={carouselSpeed * 0.8}
                options={OPTIONS}
                allowManualSwipe={true}
              >
                {row2.map((item: PlacementMetadata, idx: number) =>
                  item?.meta_image ? (
                    <div
                      key={`row2-${item?.id}-${idx}`}
                      className="w-[82px] h-[42px] flex justify-center items-center shrink-0 cursor-pointer"
                      onClick={() => handleLogoClick(item, idx + row1.length)}
                    >
                      <Image
                        src={item.meta_image || ""}
                        alt={item?.meta_alt_image || "partner logo"}
                        width={82}
                        height={42}
                        className="object-contain w-[82px] h-[42px]"
                      />
                    </div>
                  ) : null
                )}
              </Carousel8>
            )*}*/}
          </div>
        ) : (
          <Carousel8
            speed={carouselSpeed}
            options={OPTIONS}
            allowManualSwipe={false}
          >
            {activeMetadata?.map(
              (item: PlacementMetadata, idx: number) =>
                item?.meta_image && (
                  <div
                    key={`desktop-${item?.id}-${idx}`}
                    className={cn(
                      "flex justify-center items-center shrink-0 cursor-pointer",
                      "sm:w-[142px] sm:h-[56px]",
                      "md:w-[150px] md:h-[74px]",
                      "lg:w-[150px] lg:h-[74px]",
                      "xl:w-[150px] xl:h-[74px]",
                    )}
                    onClick={() => handleLogoClick(item, idx)}
                  >
                    {item?.meta_image && (
                      <Image
                        src={item?.meta_image}
                        alt={item?.meta_alt_image || "partner logo"}
                        width={144}
                        height={72}
                        className={cn("object-contain w-full h-full")}
                      />
                    )}
                  </div>
                ),
            )}
          </Carousel8>
        )}
      </div>
    </div>
  );
};

export default Placements;
